import { Component, OnInit, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpService } from '../http.service';
import { ToastrService } from 'ngx-toastr';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-campaign-survey',
  templateUrl: './campaign-survey.component.html',
  styleUrls: ['./campaign-survey.component.scss'],
})
export class CampaignSurveyComponent implements OnInit {
  selectedPurpose: string = '';
  selectedBHK: string = '';

  // URL parameters
  clickId: string = '';
  campaignId: string = '';
  userId: string = '';

  // Campaign details
  campaignDetails: any = null;
  isLoading: boolean = false;
  isSubmitting: boolean = false;

  // Description expansion
  isDescriptionExpanded: boolean = false;
  descriptionMaxLength: number = 120; // Characters for web
  mobileDescriptionMaxLength: number = 80; // Characters for mobile

  // Dynamic form data
  questions: any[] = [];
  formAnswers: { [key: string]: any } = {};

  constructor(
    private route: ActivatedRoute,
    private httpService: HttpService,
    private toastr: ToastrService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    // Retrieve URL parameters
    this.route.queryParams.subscribe((params) => {
      this.clickId = params['click_id'] || '';
      this.campaignId = params['campaign_id'] || '';
      this.userId = params['userid'] || '';

      console.log('URL Parameters:', {
        clickId: this.clickId,
        campaignId: this.campaignId,
        userId: this.userId,
      });

      // Call API to get campaign details if campaign name exists
      if (this.campaignId) {
        this.getCampaignDetails();
      }
    });
  }

  async getCampaignDetails(): Promise<void> {
    try {
      this.isLoading = true;
      const campaignDetails =
        await this.httpService.getCampaignDetailsByCampaignId(
          this.campaignId
        );
      this.campaignDetails = campaignDetails;
      console.log('Campaign Details:', this.campaignDetails);

      // Parse questionInfo from string to JSON
      if (this.campaignDetails && this.campaignDetails.questionInfo) {
        try {
          this.questions = JSON.parse(this.campaignDetails.questionInfo);
          console.log('Parsed Questions:', this.questions);

          // Log parsed answer options for debugging
          this.questions.forEach((question, index) => {
            if (this.hasAnswerOptions(question.type)) {
              // Answer is already an array, no need to parse
              const options = question.Answer || [];
              console.log(
                `Question ${index} (${question.type}): "${question.question}"`,
                'Answer Options:',
                options
              );
            }
          });

          // Initialize form answers
          this.initializeFormAnswers();
        } catch (parseError) {
          console.error('Error parsing questionInfo:', parseError);
          this.questions = [];
        }
      }
      
      // Execute campaign script if available
      if (this.campaignDetails && this.campaignDetails.campaignScript) {
        this.executeCampaignScript(this.campaignDetails.campaignScript);
      }
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    } finally {
      this.isLoading = false;
    }
  }

  initializeFormAnswers(): void {
    this.formAnswers = {};
    this.questions.forEach((question, index) => {
      if (question.type === 'Checkbox') {
        this.formAnswers[`question_${index}`] = [];
      } else if (question.type === 'Toggle') {
        this.formAnswers[`question_${index}`] = 'No';
      } else if (question.type === 'Number') {
        this.formAnswers[`question_${index}`] = '';
      } else {
        this.formAnswers[`question_${index}`] = '';
      }
    });
  }

  onAnswerChange(questionIndex: number, value: any, type: string): void {
    const key = `question_${questionIndex}`;

    if (type === 'Checkbox') {
      if (!this.formAnswers[key]) {
        this.formAnswers[key] = [];
      }

      const currentAnswers = this.formAnswers[key] as string[];
      const valueIndex = currentAnswers.indexOf(value);

      if (valueIndex > -1) {
        currentAnswers.splice(valueIndex, 1);
      } else {
        currentAnswers.push(value);
      }
    } else if (type === 'Number') {
      // Ensure it's a valid number
      const numValue = parseFloat(value);
      this.formAnswers[key] = isNaN(numValue) ? '' : numValue.toString();
    } else if (type === 'Toggle') {
      // Handle toggle as Yes/No
      this.formAnswers[key] = value;
    } else {
      // Handle Text, Radio, Dropdown
      this.formAnswers[key] = value;
    }

    console.log('Form Answers Updated:', this.formAnswers);
  }

  parseAnswerOptions(answer: any): string[] {
    try {
      // If answer is already an array, return it directly
      if (Array.isArray(answer)) {
        return answer.filter((item) => item && item.toString().trim() !== '');
      }

      // If answer is null, undefined, or empty, return empty array
      if (!answer) {
        return [];
      }

      // Convert to string for processing (backward compatibility)
      const answerString = answer.toString();

      // Clean up the string by removing newlines, tabs, and extra whitespace
      let cleanedString = answerString
        .replace(/\n/g, '')
        .replace(/\t/g, '')
        .replace(/\r/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Handle empty or just space
      if (!cleanedString || cleanedString === ' ') {
        return [];
      }

      // Handle different answer formats
      if (cleanedString.startsWith('[') && cleanedString.endsWith(']')) {
        // Array format like [\"20-30\", \"30-40\"] or ["Maharashtra", "Banglore", "Goa", "Gujarat"]
        try {
          const parsed = JSON.parse(cleanedString);
          return Array.isArray(parsed)
            ? parsed.filter((item) => item && item.trim() !== '')
            : [];
        } catch (parseError) {
          // If JSON.parse fails, try to manually extract values
          const matches = cleanedString.match(/"([^"]+)"/g);
          if (matches) {
            return matches.map((match: string) => match.replace(/"/g, ''));
          }
          return [];
        }
      } else if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
        // Single quoted value like "1000000" or "India"
        try {
          const parsed = JSON.parse(cleanedString);
          return parsed ? [parsed] : [];
        } catch (parseError) {
          // Remove quotes manually if JSON.parse fails
          return [cleanedString.replace(/"/g, '')];
        }
      } else {
        // Plain text value or malformed string
        // Try to extract quoted values first
        const matches = cleanedString.match(/"([^"]+)"/g);
        if (matches) {
          return matches.map((match: string) => match.replace(/"/g, ''));
        }
        // If no quotes found and it's not empty, return as single value
        return cleanedString ? [cleanedString] : [];
      }
    } catch (error) {
      console.error(
        'Error parsing answer options:',
        error,
        'Original answer:',
        answer
      );
      return [];
    }
  }

  hasAnswerOptions(questionType: string): boolean {
    return ['Radio', 'Dropdown', 'Checkbox', 'Toggle'].includes(questionType);
  }

  getQuestionPlaceholder(questionType: string): string {
    switch (questionType) {
      case 'Text':
        return 'Enter your answer';
      case 'Number':
        return 'Enter a number';
      default:
        return 'Enter your answer';
    }
  }
  selectPurpose(purpose: string): void {
    this.selectedPurpose = purpose;
  }

  onClose(): void {
    // Handle close action
    console.log('Close button clicked');
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  getDisplayDescription(): string {
    if (!this.campaignDetails?.campaignDescription) {
      return 'Live a life of unparalleled exclusivity at RR Wishtales, where a haven awaits you with 101 meticulously crafted units of luxurious boutique apartments. Here, life...';
    }

    const description = this.campaignDetails.campaignDescription;

    if (this.isDescriptionExpanded) {
      return description;
    }

    // Check if we're on mobile (simple check, you can make this more sophisticated)
    const isMobile = window.innerWidth <= 768;
    const maxLength = isMobile
      ? this.mobileDescriptionMaxLength
      : this.descriptionMaxLength;

    if (description.length <= maxLength) {
      return description;
    }

    return description.substring(0, maxLength) + '...';
  }

  shouldShowSeeMore(): boolean {
    if (!this.campaignDetails?.campaignDescription) {
      return true; // Show for default text
    }

    const description = this.campaignDetails.campaignDescription;
    const isMobile = window.innerWidth <= 768;
    const maxLength = isMobile
      ? this.mobileDescriptionMaxLength
      : this.descriptionMaxLength;

    return description.length > maxLength;
  }

  isFormValid(): boolean {
    // Check if all dynamic questions are answered
    for (let i = 0; i < this.questions.length; i++) {
      const key = `question_${i}`;
      const answer = this.formAnswers[key];
      const question = this.questions[i];

      // Check based on question type
      if (question.type === 'Checkbox') {
        // For checkboxes, at least one option should be selected
        if (!answer || !Array.isArray(answer) || answer.length === 0) {
          return false;
        }
      } else if (question.type === 'Toggle') {
        // For toggle, we always have a value (Yes/No), so it's always valid
        continue;
      } else {
        // For Text, Radio, Dropdown, Number
        if (!answer || answer.toString().trim() === '') {
          return false;
        }
      }
    }

    // Check fallback questions if no dynamic questions exist
    if (this.questions.length === 0) {
      if (!this.selectedPurpose || !this.selectedBHK) {
        return false;
      }
    }

    return true;
  }

  onContinue(): void {
    // Prepare question info in the required format
    const questionInfo = this.prepareQuestionInfoForSubmission();

    // Convert to string and ensure it's properly formatted
    const questionInfoString = JSON.stringify(questionInfo);
    console.log('Question Info String:', questionInfoString);

    // Prepare data for API submission
    const submissionData = {
      id: 0,
      CampaignId: this.campaignDetails?.id || 0,
      UserId: parseInt(this.userId) || 0,
      ClickId: this.clickId,
      QuestionInfo: questionInfoString,
    };

    console.log('Submitting campaign response:', submissionData);

    // Call API to submit the response
    this.submitCampaignResponse(submissionData);
  }

  prepareQuestionInfoForSubmission(): any {
    const responseData: any = {};

    // Add form answers from dynamic questions
    this.questions.forEach((question, index) => {
      const key = `question_${index}`;
      const answer = this.formAnswers[key];

      if (answer !== undefined && answer !== '' && answer !== null) {
        // For arrays (checkboxes), join them or keep as array based on requirement
        if (Array.isArray(answer) && answer.length > 0) {
          const questionKey = question.question || `question_${index}`;
          responseData[questionKey] = answer;
        } else if (!Array.isArray(answer)) {
          const questionKey = question.question || `question_${index}`;
          responseData[questionKey] = answer;
        }
      }
    });

    // Add legacy answers if they exist (fallback questions)
    if (this.selectedPurpose) {
      responseData['Purpose of Buying'] = this.selectedPurpose;
    }

    if (this.selectedBHK) {
      responseData['Preferred BHK'] = this.selectedBHK;
    }

    console.log('Prepared Response Data:', responseData);
    return responseData;
  }

  async submitCampaignResponse(data: any): Promise<void> {
    try {
      console.log('Starting campaign submission...');
      this.isLoading = true;
      this.isSubmitting = true;

      console.log('Calling API with data:', data);
      const response = await this.httpService.insertUpdateCampaignSubmission(
        data
      );
      console.log('Campaign submission response:', response);
      console.log('HTTP Service success flag:', this.httpService.success);

      // Handle success - you can add navigation or success message here
      if (response && this.httpService.success) {
        console.log('Campaign response submitted successfully!');
        this.toastr.success('Survey submitted successfully!');
        // You can add navigation to success page or show success message
      } else {
        console.error(
          'Failed to submit campaign response ',
          response.error.message
        );
        this.toastr.error(response.error.message || 'Submission failed');
        this.isSubmitting = false; // Re-enable button on failure
      }
    } catch (error) {
      // /console.error('Error submitting campaign response:', error);
      // this.toastr.error(String(error));
      this.isSubmitting = false; // Re-enable button on error
    } finally {
      this.isLoading = false;
      console.log('Campaign submission completed');
    }
  }
  
  /**
   * Executes campaign script safely by injecting it into the document
   * @param scriptContent The HTML/JavaScript content to execute
   */
  private executeCampaignScript(scriptContent: string): void {
    try {
      console.log('Executing campaign script:', scriptContent);
      
      // Method 1: Direct script evaluation (if the script is just JavaScript code)
      if (!scriptContent.includes('<script>') && !scriptContent.includes('</script>')) {
        try {
          // Create a new script element and execute it
          const scriptEl = this.renderer.createElement('script');
          const scriptText = this.renderer.createText(scriptContent);
          this.renderer.appendChild(scriptEl, scriptText);
          this.renderer.appendChild(this.document.head, scriptEl);
          console.log('Direct script execution completed');
          return;
        } catch (evalError: any) {
          console.error('Error in direct script execution:', evalError);
          // Continue with other methods
        }
      }
      
      // Method 2: Parse HTML and extract scripts
      const tempDiv = this.renderer.createElement('div');
      tempDiv.innerHTML = scriptContent;
      
      // Find all script tags in the content
      const scriptTags = tempDiv.getElementsByTagName('script');
      
      // Execute each script
      if (scriptTags.length > 0) {
        console.log(`Found ${scriptTags.length} script tags to execute`);
        
        for (let i = 0; i < scriptTags.length; i++) {
          const scriptTag = scriptTags[i];
          const scriptText = scriptTag.textContent || scriptTag.innerText;
          
          if (scriptText) {
            // Create a new script element with all original attributes
            const newScriptEl = this.renderer.createElement('script');
            
            // Copy attributes from original script tag
            for (let j = 0; j < scriptTag.attributes.length; j++) {
              const attr = scriptTag.attributes[j];
              this.renderer.setAttribute(newScriptEl, attr.name, attr.value);
            }
            
            // Set the script content
            const newScriptText = this.renderer.createText(scriptText);
            this.renderer.appendChild(newScriptEl, newScriptText);
            
            // Add the new script element to the document head
            this.renderer.appendChild(this.document.head, newScriptEl);
            console.log(`Script ${i+1} added to document head`);
          }
        }
      } else {
        // Method 3: Try direct HTML injection if no script tags found
        console.warn('No script tags found in campaign script content, trying direct HTML injection');
        
        // Create a div to hold the entire HTML content
        const containerDiv = this.renderer.createElement('div');
        this.renderer.setProperty(containerDiv, 'innerHTML', scriptContent);
        this.renderer.appendChild(this.document.body, containerDiv);
        
        // Look for scripts in the newly created element
        const insertedScripts = containerDiv.getElementsByTagName('script');
        if (insertedScripts.length > 0) {
          console.log(`Found ${insertedScripts.length} scripts after HTML injection`);
        }
        
        // Method 4: Last resort - manual execution of the entire content as script
        try {
          // Extract any JavaScript code outside of HTML tags
          const scriptPattern = /<script[^>]*>([\s\S]*?)<\/script>/gi;
          const scriptMatches = scriptContent.match(scriptPattern);
          
          if (scriptMatches && scriptMatches.length > 0) {
            scriptMatches.forEach((match, index) => {
              // Extract script content
              const scriptCode = match.replace(/<script[^>]*>|<\/script>/gi, '');
              
              if (scriptCode.trim()) {
                // Create a script element
                const finalScript = this.renderer.createElement('script');
                const scriptTextNode = this.renderer.createText(scriptCode);
                this.renderer.appendChild(finalScript, scriptTextNode);
                this.renderer.appendChild(this.document.head, finalScript);
                console.log(`Manual script ${index+1} executed`);
              }
            });
          }
        } catch (manualError: any) {
          console.error('Error in manual script execution:', manualError);
        }
      }
    } catch (error: any) {
      console.error('Error executing campaign script:', error);
    }
  }
    
}
